// BL-177 Step 2d (section 2): ONE meaning per colour, always with words. Shared tokens for every screen.
// Red = act now · Orange = check this · Yellow = someone's on it · Blue = come back to it ·
// Purple = time-based · Green = done · Grey = no action · Teal = us · Slate = them.
// Channels (SMS / WhatsApp / Email) deliberately have NO colour: icon + word in white.
//
// `bg`/`border` are fixed hex (not theme tokens) so white text holds >= 4.5:1 in both skins.
// The same values are mirrored as --ia-tone-* custom properties in src/styles/global.css.
export const TONES = Object.freeze({
    red: { bg: '#B91C1C', border: '#EF4444', meaning: 'Act now' },
    orange: { bg: '#C2410C', border: '#FB923C', meaning: 'Check this' },
    yellow: { bg: '#A16207', border: '#FACC15', meaning: "Someone's on it" },
    blue: { bg: '#1D4ED8', border: '#60A5FA', meaning: 'Come back to it' },
    purple: { bg: '#6D28D9', border: '#A78BFA', meaning: 'Time-based' },
    green: { bg: '#15803D', border: '#4ADE80', meaning: 'Done' },
    grey: { bg: '#475569', border: '#94A3B8', meaning: 'No action needed' },
    teal: { bg: '#0F766E', border: '#2DD4BF', meaning: 'Us' },
    slate: { bg: '#334155', border: '#64748B', meaning: 'Them' },
});

// Conversation flags (keys are the stored flag keys; labels are the words people see).
export const FLAG_TONES = Object.freeze({
    urgent: { tone: 'red', label: 'Urgent' },
    asap: { tone: 'red', label: 'ASAP' },
    needs_attention: { tone: 'orange', label: 'Needs attention' },
    in_progress: { tone: 'yellow', label: 'In progress' },
    follow_up: { tone: 'blue', label: 'Follow up' },
    resolved: { tone: 'green', label: 'Resolved' },
    closed: { tone: 'grey', label: 'Closed' },
});

// Delivery states show ONLY on the message itself.
export const DELIVERY_TONES = Object.freeze({
    not_delivered: { tone: 'red', label: 'Not delivered' },
    unconfirmed: { tone: 'orange', label: 'Unconfirmed' },
    scheduled: { tone: 'purple', label: 'Scheduled' },
    // BL-177 Step 3: a scheduled message the system stopped (recipient opted out, or they replied first): shown, with the reason.
    cancelled: { tone: 'grey', label: 'Cancelled' },
});

// Row tags on a conversation.
export const TAG_TONES = Object.freeze({
    after_hours: { tone: 'purple', label: 'After hours' },
    scheduled: { tone: 'purple', label: 'Scheduled' },
    group_text: { tone: 'grey', label: 'Group text' },
    short_code: { tone: 'grey', label: 'Short code' },
    imported_legacy: { tone: 'grey', label: 'Imported (legacy)' },
    saved: { tone: 'green', label: 'Saved ✓' },
});

// Bubbles: teal = us, slate = them.
export const BUBBLE_TONES = Object.freeze({ outbound: 'teal', inbound: 'slate' });

// Stored flag keys vary (spaces, dashes, case). One normaliser so every screen agrees.
export function normalizeFlagKey(value) {
    return String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
}

// Older keys that already exist in data, mapped onto the new set (org-custom colours are replaced by this system).
const FLAG_ALIASES = Object.freeze({ followup: 'follow_up', follow_through: 'needs_attention', followthru: 'needs_attention', needs_attn: 'needs_attention' });

export function flagMeaning(flagKey) {
    const key = normalizeFlagKey(flagKey);
    return FLAG_TONES[FLAG_ALIASES[key] || key] || null;
}

export function toneStyle(tone) {
    const t = TONES[tone] || TONES.grey;
    return { background: t.bg, borderColor: t.border, color: '#FFFFFF' };
}
